<template>
	<footer class="footer">
		<ContainedSection
			:has-padding="true"
			:has-background="true"
			background-color="bg-gray-100"
			contained-class="contained-section"
		>
			<div class="flex w-full flex-wrap lg:w-full">
				<div class="flex w-full flex-wrap space-y-8 lg:space-y-0">
					<template v-for="menu in menus" :key="menu.title">
						<div class="w-full space-y-4 lg:w-3/12">
							<h4 class="font-body text-lg font-medium">
								{{ menu.title }}
							</h4>
							<ul class="m-0 flex list-none flex-col space-y-3">
								<li
									v-for="menuItem in menu.items"
									:key="menuItem.label"
								>
									<CarbarLink
										font-size="text-base"
										:href="menuItem.link"
										:underline="false"
										:target="menuItem.target"
										v-bind="menuItem.attributes"
										:text="menuItem.label"
									/>
								</li>
							</ul>
							<div v-if="menu.business" class="space-y-4">
								<h4 class="mt-8 font-body text-lg font-medium">
									{{ menu.business.title }}
								</h4>
								<ul
									class="m-0 flex list-none flex-col space-y-3"
								>
									<li
										v-for="menuItem in menu.business.items"
										:key="menuItem.label"
									>
										<CarbarLink
											font-size="text-base"
											:href="menuItem.link"
											:underline="false"
											v-bind="menuItem.attributes"
											:text="menuItem.label"
										/>
									</li>
								</ul>
							</div>
							<div
								v-if="menu.socmed"
								class="mt-4 flex flex-row items-center gap-4"
							>
								<NuxtLink
									href="https://www.facebook.com/CarbarComAu"
									target="_blank"
								>
									<CarbarImage
										image="img/socials/img-fb.webp"
										alt-text="facebook"
										image-width-class="w-6"
									/>
								</NuxtLink>
								<NuxtLink
									href="https://www.instagram.com/carbar/"
									target="_blank"
								>
									<CarbarImage
										image="img/socials/img-insta.webp"
										alt-text="instagram"
										image-width-class="w-6"
									/>
								</NuxtLink>
								<NuxtLink
									href="https://www.linkedin.com/company/carbar/"
									target="_blank"
								>
									<CarbarImage
										image="img/socials/img-linkedin.webp"
										alt-text="linkedin"
										image-width-class="w-6"
									/>
								</NuxtLink>
							</div>
						</div>
					</template>

					<div class="w-full space-y-4 lg:w-3/12">
						<h4 class="font-body text-lg font-medium">Support</h4>
						<ul
							class="m-0 flex list-none flex-col space-y-2 lg:space-y-3"
						>
							<li>
								<CarbarLink
									target="_blank"
									href="https://support.carbar.com.au"
									font-size="text-base"
									:underline="false"
									text="Help and support line"
								/>
							</li>
							<li>
								<hr />
							</li>
							<li class="flex flex-col space-y-1">
								<CarbarLink
									target="_blank"
									href="https://support.carbar.com.au"
									font-size="text-base"
									:underline="false"
									text="Or get in touch"
								/>
								<CarbarLink
									:underline="false"
									font-size="text-base"
									href="tel:1300 620 685"
									text="1300 620 685"
								/>

								<CarbarLink
									:underline="false"
									font-size="text-base"
									href="mailto:support@carbar.com.au"
									text="support@carbar.com.au"
								/>
							</li>
							<li class="flex flex-col space-y-1">
								<p class="text-base">
									Mon - Fri 9:00am - 5:00pm
								</p>
								<p class="text-base">Public holidays closed</p>
							</li>
							<li>
								<hr />
							</li>
							<li>
								<CarbarLink
									target="_blank"
									href="https://support.carbar.com.au"
									font-size="text-base"
									:underline="false"
									text="Roadside Assist"
								/>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</ContainedSection>
	</footer>
</template>

<script setup>
import CarbarLink from '@/components/links/CarbarLink.vue'
import ContainedSection from '@/components/sections/ContainedSection.vue'
import CarbarImage from '@/components/CarbarImage.vue'

const emits = defineEmits(['login', 'logout', 'join'])

const route = useRoute()

const isHomepage = computed(() => route.path === '/')

const groupSubsBasePath = '/partners/carsales/group-subscription'
const isGroupSubscriptionEnabled = useIsGroupSubscriptionEnabled()

const menus = [
	{
		title: 'Personal',
		items: [
			{
				label: 'Car subscription & how it works',
				link: '/car-subscription',
				current: false,
			},
			{
				label: 'Electric car subscription',
				link: '/electric-vehicles',
				current: false,
			},
			{
				label: 'Trade in to subscribe',
				link: '/sell-my-car',
				current: false,
			},
			{
				label: 'Long term car rental',
				link: '/car-long-term-rental',
				current: false,
			},
			{
				label: 'Car lease',
				link: '/car-lease',
				current: false,
			},
			{
				label: 'Rent-to-own cars',
				link: '/car-rent-to-own',
				current: false,
			},
			...(isGroupSubscriptionEnabled
				? [
						{
							label: 'Group subscription',
							link: isHomepage.value
								? `${groupSubsBasePath}?ref=homepage`
								: groupSubsBasePath,
							current: false,
						},
					]
				: []),
		],
		business: {
			title: 'Business',
			items: [
				{
					label: 'Novated lease',
					link: '/business/novated-lease',
					current: false,
				},
				{
					label: 'Novated subscription',
					link: '/business/novated-subscription',
					current: false,
				},
				{
					label: 'Novated EV subscription',
					link: '/business/novated-ev-subscription',
					current: false,
				},
				{
					label: 'Fleet subscription',
					link: '/business/fleet-subscription',
					current: false,
				},
				{
					label: 'Fleet subscription for small business',
					link: '/business/fleet-subscription/small-business',
					current: false,
				},
				{
					label: 'Employee benefits',
					link: '/business/tools-of-trade/employee-benefits',
					current: false,
				},
				{
					label: 'Car subscription for dealers',
					link: '/dealers',
					current: false,
				},
			],
		},
	},
	{
		title: 'Company',
		items: [
			{ label: 'About us', link: '/about-us', current: false },
			{ label: 'Press', link: '/press', current: false },
			{ label: 'carbar Brisbane', link: '/brisbane', current: false },
			{ label: 'carbar Melbourne', link: '/melbourne', current: false },
			{ label: 'carbar Sydney', link: '/sydney', current: false },
			{ label: 'Careers', link: '/about-us/careers', current: false },
			{ label: 'Dealers', link: '/dealers', current: false },
			{
				label: 'Blog',
				link: 'https://www.carbar.com.au/blog',
				current: false,
				target: '_blank',
			},
		],
		socmed: true,
	},
	{
		title: 'Important Information',
		items: [
			{
				label: 'Terms & conditions',
				link: '/terms-and-conditions',
				current: false,
			},
			{
				label: 'Privacy policy',
				link: '/privacy-policy',
				current: false,
			},
			{
				label: 'Fees & charges',
				link: '/car-subscription/fees-and-charges',
				current: false,
			},
			{ label: 'Sitemap', link: '/sitemap', current: false },
		],
	},
]
</script>
